<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useMenuStore } from '@/stores/menu'
import { useSysBrandingStore } from '@/stores/sysBranding'
import { resolveMenuChildPath } from '@/layouts/config'
import { SidebarMenuSkeleton } from '@/components/skeleton'

const props = defineProps<{
  collapsed: boolean
  mobile?: boolean
  mobileOpen?: boolean
}>()

const emit = defineEmits<{
  close: []
}>()

const route = useRoute()
const router = useRouter()
const menuStore = useMenuStore()
const sysBranding = useSysBrandingStore()

const menuCollapsed = computed(() => (props.mobile ? false : props.collapsed))
const showBrandText = computed(() => !menuCollapsed.value)
const menus = computed(() => menuStore.menus)

const openKeys = ref<string[]>([])

function isChildRouteActive(child: { key: string; path: string }, currentPath: string): boolean {
  if (child.path === currentPath) return true
  if (child.key === 'case' && currentPath.startsWith('/emergency/case')) return true
  if (child.key === 'evaluation' && currentPath.startsWith('/emergency/evaluation')) return true
  if (child.path.endsWith('/list')) {
    const basePath = child.path.slice(0, -'/list'.length)
    if (basePath && currentPath.startsWith(`${basePath}/`)) return true
  }
  return false
}

/** 当前选中的菜单 key（支持子菜单） */
const selectedKeys = computed(() => {
  const path = route.path
  for (const menu of menuStore.menus) {
    if (menu.children) {
      for (const child of menu.children) {
        if (isChildRouteActive(child, path)) {
          return [child.key]
        }
      }
    } else if (menu.path === path) {
      return [menu.key]
    }
  }
  return []
})

function handleMenuSelect(info: { key: string | number }) {
  const path = findPathByKey(String(info.key))
  if (path) router.push(path)
  if (props.mobile) emit('close')
}

/** 悬停时预拉取对应路由 chunk，减轻首次点击等待感 */
function prefetchRouteByKey(key: string) {
  const path = findPathByKey(key)
  if (!path) return
  const resolved = router.resolve(path)
  const matched = resolved.matched[resolved.matched.length - 1]
  const comp = matched?.components?.default
  if (typeof comp === 'function') {
    void (comp as () => Promise<unknown>)()
  }
}

function findPathByKey(key: string): string | undefined {
  for (const menu of menuStore.menus) {
    if (menu.key === key) return menu.path
    if (menu.children) {
      const child = menu.children.find((c) => c.key === key)
      if (child?.path) return resolveMenuChildPath(child.key, child.path, undefined, child.label)
    }
  }
  return undefined
}

onMounted(() => {
  menuStore.loadMenus()
})

watch(
  () => [route.path, menuStore.menus] as const,
  () => {
    const path = route.path
    for (const menu of menuStore.menus) {
      if (menu.children?.some((c) => isChildRouteActive(c, path))) {
        openKeys.value = [menu.key]
        return
      }
    }
    const first = menuStore.menus.find((m) => m.children?.length)
    if (first) openKeys.value = [first.key]
  },
  { immediate: true },
)
</script>

<template>
  <aside
    class="app-sidebar"
    :class="{
      collapsed: menuCollapsed,
      mobile: mobile,
      'mobile-open': mobile && mobileOpen,
    }"
  >
    <div class="sidebar-brand" :class="{ 'sidebar-brand--collapsed': menuCollapsed && !mobile }">
      <div class="sidebar-brand-mark">
        <img
          class="sidebar-brand-logo"
          :src="sysBranding.logoUrlOrDefault"
          alt=""
          referrerpolicy="no-referrer"
        />
      </div>
      <div v-if="showBrandText" class="sidebar-brand-meta">
        <span class="sidebar-brand-kicker">功能导航</span>
        <span class="sidebar-brand-hint">业务模块入口</span>
      </div>
    </div>

    <div class="sidebar-spin">
      <div class="sidebar-spin-body">
        <SidebarMenuSkeleton v-if="menuStore.loading && !menus.length" />
        <template v-else-if="menus.length">
          <a-menu
            mode="inline"
            :inline-collapsed="menuCollapsed"
            :selected-keys="selectedKeys"
            :open-keys="menuCollapsed ? [] : openKeys"
            class="sidebar-menu"
            @select="handleMenuSelect"
            @update:open-keys="openKeys = $event as string[]"
          >
            <template v-for="menu in menus" :key="menu.key">
              <a-sub-menu v-if="menu.children" :key="menu.key" :title="menu.label">
                <template #icon>
                  <span class="sidebar-menu-icon-wrap">
                    <component :is="menu.icon" />
                  </span>
                </template>
                <template #title>
                  <span class="sidebar-menu-group-title">{{ menu.label }}</span>
                </template>
                <a-menu-item
                  v-for="child in menu.children"
                  :key="child.key"
                  :title="child.label"
                  @mouseenter="prefetchRouteByKey(child.key)"
                >
                  <template #icon>
                    <span class="sidebar-menu-icon-wrap sidebar-menu-icon-wrap--sub">
                      <component :is="child.icon" v-if="child.icon" />
                    </span>
                  </template>
                  <span class="sidebar-menu-item-label">{{ child.label }}</span>
                </a-menu-item>
              </a-sub-menu>

              <a-menu-item
                v-else
                :key="menu.key"
                :title="menu.label"
                @mouseenter="prefetchRouteByKey(menu.key)"
              >
                <template #icon>
                  <span class="sidebar-menu-icon-wrap">
                    <component :is="menu.icon" />
                  </span>
                </template>
                <span class="sidebar-menu-item-label">{{ menu.label }}</span>
              </a-menu-item>
            </template>
          </a-menu>
        </template>
        <div v-else class="sidebar-empty">
          <a-empty description="暂无可访问菜单" />
        </div>
      </div>
    </div>
  </aside>
</template>

<style scoped>
.app-sidebar {
  width: var(--sidebar-width);
  min-width: var(--sidebar-width);
  height: 100%;
  display: flex;
  flex-direction: column;
  background: var(--nav-sidebar-bg);
  border-right: 1px solid var(--nav-sidebar-border);
  transition:
    width 0.2s ease,
    min-width 0.2s ease,
    transform 0.25s ease,
    background 0.25s ease,
    border-color 0.25s ease;
  flex-shrink: 0;
  overflow: hidden;
  font-family: var(--nav-font-ui);
}

.app-sidebar.collapsed:not(.mobile) {
  width: var(--sidebar-width-collapsed);
  min-width: var(--sidebar-width-collapsed);
}

.app-sidebar.mobile {
  position: fixed;
  top: var(--layout-header-height);
  left: 0;
  bottom: 0;
  z-index: 1100;
  width: var(--sidebar-drawer-width) !important;
  min-width: 0 !important;
  max-width: var(--sidebar-drawer-width);
  transform: translateX(-100%);
  box-shadow: var(--shadow-lg);
  border-right: none;
}

.app-sidebar.mobile.mobile-open {
  transform: translateX(0);
}

@media (max-width: 767px) {
  .app-sidebar:not(.mobile) {
    display: none;
  }
}

/* ---------- 侧栏品牌区 ---------- */
.sidebar-brand {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 14px 12px;
  margin: 0 10px;
  border-bottom: 1px solid var(--ds-border-subtle);
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.4) 0%, transparent 100%);
}

html[data-theme='dark'] .sidebar-brand {
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.04) 0%, transparent 100%);
}

.sidebar-brand--collapsed {
  flex-direction: column;
  justify-content: center;
  padding: 14px 8px 12px;
  margin: 0 6px;
  gap: 0;
}

.sidebar-brand-mark {
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  background: var(--nav-sidebar-icon-bg);
  border: 1px solid var(--ds-border-subtle);
  box-shadow: var(--shadow-sm);
  overflow: hidden;
}

.sidebar-brand--collapsed .sidebar-brand-mark {
  width: 36px;
  height: 36px;
  border-radius: 8px;
}

.sidebar-brand-logo {
  width: 28px;
  height: 28px;
  object-fit: contain;
}

.sidebar-brand-meta {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.sidebar-brand-kicker {
  font-size: 13px;
  font-weight: 600;
  color: var(--nav-sidebar-group-text);
  letter-spacing: 0.04em;
  line-height: 1.3;
}

.sidebar-brand-hint {
  font-size: 11px;
  color: var(--nav-sidebar-text-muted);
  line-height: 1.3;
}

/* ---------- 菜单滚动区 ---------- */
.sidebar-menu {
  flex: 1;
  min-height: 0;
  max-height: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  border-inline-end: none !important;
  padding: 10px 0 16px;
  -webkit-overflow-scrolling: touch;
}

.sidebar-menu :deep(.ant-menu) {
  background: transparent !important;
  color: var(--nav-sidebar-text);
}

.sidebar-menu :deep(.ant-menu-submenu-title) {
  color: var(--nav-sidebar-group-text);
  font-weight: 600;
  font-size: 13px;
  letter-spacing: 0.02em;
}

.sidebar-menu :deep(.ant-menu-item),
.sidebar-menu :deep(.ant-menu-submenu-title) {
  height: 44px;
  line-height: 44px;
  margin: 2px 8px;
  border-radius: 10px;
  padding-inline: 10px !important;
  cursor: pointer;
  transition:
    color 0.2s ease,
    background 0.2s ease,
    box-shadow 0.2s ease;
}

.sidebar-menu :deep(.ant-menu-submenu-arrow) {
  color: var(--nav-sidebar-text-muted);
  transition: color 0.2s ease;
}

.sidebar-menu :deep(.ant-menu-submenu-open > .ant-menu-submenu-title .ant-menu-submenu-arrow) {
  color: var(--app-primary);
}

/* 图标容器 */
.sidebar-menu-icon-wrap {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: var(--nav-sidebar-icon-bg);
  color: var(--nav-sidebar-text);
  font-size: 16px;
  transition:
    background 0.2s ease,
    color 0.2s ease;
}

.sidebar-menu-icon-wrap--sub {
  width: 28px;
  height: 28px;
  font-size: 14px;
  background: transparent;
}

.sidebar-menu-group-title {
  font-weight: 600;
}

.sidebar-menu-item-label {
  font-size: 14px;
  font-weight: 500;
}

/* 选中态 */
.sidebar-menu :deep(.ant-menu-item-selected) {
  position: relative;
  background: var(--nav-sidebar-active-bg) !important;
  color: var(--nav-sidebar-text-active) !important;
  font-weight: 600;
  box-shadow: inset 0 0 0 1px rgba(3, 105, 161, 0.12);
}

.sidebar-menu :deep(.ant-menu-item-selected .sidebar-menu-icon-wrap) {
  background: var(--nav-sidebar-icon-bg-active);
  color: var(--nav-sidebar-text-active);
}

.sidebar-menu :deep(.ant-menu-item-selected::before) {
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 3px;
  height: 22px;
  background: var(--nav-sidebar-active-bar);
  border-radius: 0 3px 3px 0;
  content: '';
}

/* 悬停 */
.sidebar-menu :deep(.ant-menu-item:hover),
.sidebar-menu :deep(.ant-menu-submenu-title:hover) {
  color: var(--nav-sidebar-text-active);
  background: var(--nav-sidebar-hover) !important;
}

.sidebar-menu :deep(.ant-menu-item:hover .sidebar-menu-icon-wrap),
.sidebar-menu :deep(.ant-menu-submenu-title:hover .sidebar-menu-icon-wrap) {
  background: var(--nav-sidebar-icon-bg-active);
  color: var(--nav-sidebar-text-active);
}

/* 子菜单面板 */
.sidebar-menu :deep(.ant-menu-sub) {
  background: var(--nav-sidebar-sub-bg) !important;
  margin: 2px 8px 6px;
  padding: 4px 0;
  border-radius: 10px;
}

.sidebar-menu :deep(.ant-menu-sub .ant-menu-item) {
  height: 40px;
  line-height: 40px;
  margin: 2px 6px;
  padding-inline-start: 10px !important;
}

.sidebar-menu :deep(.ant-menu-sub .ant-menu-item .sidebar-menu-item-label) {
  font-size: 13px;
  font-weight: 500;
}

/* 键盘焦点 */
.sidebar-menu :deep(.ant-menu-item:focus-visible),
.sidebar-menu :deep(.ant-menu-submenu-title:focus-visible) {
  outline: 2px solid var(--app-primary);
  outline-offset: 1px;
}

/* 收起态 */
.app-sidebar.collapsed:not(.mobile) .sidebar-menu :deep(.ant-menu-item),
.app-sidebar.collapsed:not(.mobile) .sidebar-menu :deep(.ant-menu-submenu-title) {
  padding-inline: 8px !important;
  display: flex;
  justify-content: center;
  margin: 4px 6px;
}

.app-sidebar.collapsed:not(.mobile) .sidebar-menu :deep(.ant-menu-item .ant-menu-title-content),
.app-sidebar.collapsed:not(.mobile)
  .sidebar-menu
  :deep(.ant-menu-submenu-title .ant-menu-title-content) {
  display: none;
}

.app-sidebar.collapsed:not(.mobile) .sidebar-menu :deep(.ant-menu-submenu-arrow) {
  display: none;
}

.app-sidebar.collapsed:not(.mobile) .sidebar-menu-icon-wrap {
  width: 36px;
  height: 36px;
}

.sidebar-spin {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  width: 100%;
  align-self: stretch;
}

.sidebar-spin :deep(.ant-spin-container) {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.sidebar-spin-body {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.sidebar-loading-fill {
  flex: 1;
  min-height: 120px;
}

.sidebar-empty {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 32px 16px;
}

@media (prefers-reduced-motion: reduce) {
  .sidebar-menu :deep(.ant-menu-item),
  .sidebar-menu :deep(.ant-menu-submenu-title),
  .sidebar-menu-icon-wrap,
  .app-sidebar {
    transition: none !important;
  }
}
</style>
